import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import { LoadingButton } from '@mui/lab';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Card,
    CardMedia,
    Checkbox,
    CircularProgress,
    Container,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import * as lodash from 'lodash';
import { MuiColorInput } from 'mui-color-input';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { EStatusCode } from '../../api/enum';
import { IResponse } from '../../api/httpClient';
import * as ProductHelper from '../../api/product/productHelper';
import { drainHeight, drainUnitLength, drainUnitWidth, maximumAppliedLoad, maxUnitaryDrainageCapacity, productPD } from '../../conversion/product';
import { ELanguages, languages, TLanguages } from '../../localization';
import { route } from '../../route/Index';
import DropDown from '../../shared/component/DropDown';
import Input from '../../shared/component/Input';
import { EStatus, EUnitOfMeasure } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { EDrainageCapacityType, IDrainageCapacityProduct, IProduct, IProductPhysicalDatas, ITranslation, TInputProductPhysicalDatas } from '../../shared/iProject';
import variable from '../../shared/style/variable';
import { toastAlert } from '../../shared/util';
import { useAppSelector } from '../../store/hook';
import { selectProject } from '../../store/project/projectSlice';
import ModalDrainageCapacity from './ModalDrainageCapacity';

interface IProductDetailProps {}

const ProductContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

let translations: ITranslation[] = [];

let newPhysicalDataMetric: IProductPhysicalDatas = {
    unitOfMeasure: EUnitOfMeasure.Metric,
    drainHeight: 0,
    drainUnitWidth: 0,
    drainUnitLength: 0,
    maxUnitaryDrainageCapacity: 0,
    maximumAppliedLoad: 0,
};

let newPhysicalDataImperial: IProductPhysicalDatas = {
    unitOfMeasure: EUnitOfMeasure.Imperial,
    drainHeight: 0,
    drainUnitWidth: 0,
    drainUnitLength: 0,
    maxUnitaryDrainageCapacity: 0,
    maximumAppliedLoad: 0,
};

let newProduct: IProduct = {
    isBase: true,
    status: EStatus.Test,
    isMaccaferriManufacturer: true,
    color: 'red',
    hasPipe: false,
    drainageCapacities: [
        {
            position: EDrainageCapacityType.Vertical,
            islColumns: [0.1, 0.2, 0.3, 0.4, 0.6],
            appliedLoadsRows: [2, 20, 100, 200, 400],
            values: [
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 49.8],
            ],
        },
        {
            position: EDrainageCapacityType.Horizontal,
            islColumns: [0.1, 0.2, 0.3, 0.4, 0.6],
            appliedLoadsRows: [2, 20, 100, 200, 400],
            values: [
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 43.8],
                [9.02, null, null, 28.2, 49.8],
            ],
        },
    ],
    productPhysicalDatas: [newPhysicalDataMetric, newPhysicalDataImperial],
    translations: [
        {
            iso: 'en',
            name: 'New Product',
        },
        {
            iso: 'it',
            name: 'New Product',
        },
    ],
};

const ProductDetail = (props: IProductDetailProps) => {
    let { param } = useParams();
    const project = useAppSelector(selectProject);

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const [unitOfMeasure, setUnitOfMeasure] = React.useState<EUnitOfMeasure>(EUnitOfMeasure.Metric);

    const navigate = useNavigate();

    const [openModal, setOpenModal] = React.useState(false);

    const { t } = useTranslation();

    const [product, setProduct] = React.useState<IProduct | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaveLoading, setIsSaveLoading] = React.useState(false);
    const [image, setImage] = React.useState<any[]>(['']);

    React.useEffect(() => {
        const fetchProduct = async () => {
            setIsLoading(true);
            if (param === 'newProduct') {
                setProduct(newProduct);
                setImage(['']);
                translations = lodash.cloneDeep(newProduct.translations!);
            } else {
                let productId = parseInt(param!);
                let product = await ProductHelper.getProductByIdAsync(productId);
                setProduct(product.data);
                setImage(product.data.images!.map(x => x.imageUrl));
                translations = lodash.cloneDeep(product.data.translations!);
            }
            setIsLoading(false);
        };
        fetchProduct();
    }, []);

    const handleClickSave = async () => {
        let payload: IProduct = {
            ...product!,
            translations,
            images: product!.images!.filter(x => image.some(y => y == x.imageUrl)),
        };
        setIsSaveLoading(true);
        const formData = new FormData();
        formData.append('EntityJson', JSON.stringify(payload!));
        image?.forEach(x => {
            if (typeof x !== 'string') formData.append(`Images`, x);
        });
        let res: IResponse<IProduct>;
        if (param === 'newProduct') {
            res = await ProductHelper.insertProductWithImageAsync(formData!);
        } else {
            res = await ProductHelper.updateProductWithImageAsync(formData!);
        }
        if (res.status == EStatusCode.Ok) {
            toastAlert('success', t('savedProduct'));
        }
        setIsSaveLoading(false);
    };

    const handleChangeTraslation = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let index = translations.findIndex(x => x.iso === name);
        if (index !== -1) {
            translations[index].name = value;
        } else {
            translations.push({
                iso: name,
                name: value,
            });
        }
    };

    const handleClickRemoveTranslation = (iso: string) => (translations = translations.filter(x => x.iso !== iso));

    const handleImageChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        const file = event.target.files[0];
        let t = lodash.cloneDeep(image);
        t[index] = file;
        setImage(t);
    };

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, id: number) => {
        let { name, value } = event.target;
        let t = lodash.cloneDeep(product);
        let index = t!.productPhysicalDatas?.findIndex(x => x.id == id)!;
        t!.productPhysicalDatas![index] = {
            ...t!.productPhysicalDatas![index],
            [name]: parseFloat(value),
        };
        setProduct(t);
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleClickApply = (drainageCapacities: IDrainageCapacityProduct[]) => {
        setProduct({ ...product, drainageCapacities });
        toastAlert('success', t('updatedDrainageCapacity'));
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <Link to={route.backoffice} style={{ textDecoration: 'none' }}>
                    <Stack alignItems={'center'} direction={'row'}>
                        <ArrowBackIosIcon style={{ fill: variable.greenLight }} />
                        <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                            {t('back')}
                        </Typography>
                    </Stack>
                </Link>
                <Stack width={'100%'} textAlign={'center'}>
                    <Typography variant='h6' color={variable.greenLight}>
                        {param == 'newProduct' ? t('addNewProduct') : t('updateProduct')}
                    </Typography>
                </Stack>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && product && (
                <>
                    <ProductContainer maxWidth={'xl'}>
                        <Stack height={'95%'} direction='column'>
                            <Stack padding={'0 1rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                <Stack direction={'row'} alignItems={'center'}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={product?.status == EStatus.Public}
                                                onChange={(e: any) => setProduct({ ...product!, status: e.target.checked ? EStatus.Public : EStatus.Test })}
                                            />
                                        }
                                        label={t('public')}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={product?.isMaccaferriManufacturer}
                                                onChange={(e: any) =>
                                                    setProduct({
                                                        ...product!,
                                                        isMaccaferriManufacturer: e.target.checked,
                                                        productPhysicalDatas: e.target.checked ? product.productPhysicalDatas : [product.productPhysicalDatas![0]],
                                                    })
                                                }
                                            />
                                        }
                                        label={t('maccaferriManufacturer')}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={product?.hasPipe} onChange={(e: any) => setProduct({ ...product!, hasPipe: e.target.checked })} />}
                                        label={t('hasDrainagePiping')}
                                    />
                                    <Stack flexDirection={'row'} gap={1}>
                                        <InputLabel style={{ alignContent: 'center', color: 'black' }}>{t('unitOfMeasure')}</InputLabel>
                                        <DropDown value={unitOfMeasure} onChange={e => setUnitOfMeasure(e.target.value as any)}>
                                            <MenuItem value={EUnitOfMeasure.Metric}>{t('metric')}</MenuItem>
                                            <MenuItem value={EUnitOfMeasure.Imperial}>{t('imperial')}</MenuItem>
                                        </DropDown>
                                    </Stack>
                                </Stack>
                                <MuiColorInput value={product?.color!} onChange={color => setProduct({ ...product!, color })} />
                            </Stack>
                            <Stack direction={'row'}>
                                <Button variant='green' style={{ margin: '0.5rem 0' }} onClick={() => setOpenModal(true)}>
                                    {t('changeDrainageCapacity')}
                                </Button>
                            </Stack>
                            <Stack height={'100%'} direction={'row'} spacing={1}>
                                <Stack width={'33%'} position='relative'>
                                    <Stack variant='scrollable'>
                                        {Object.keys(languages).map(x => (
                                            <Accordion disableGutters>
                                                <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                    <Typography>{languages[x as TLanguages]}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Stack direction={'column'} spacing={2}>
                                                        <Input defaultValue={product!.translations!.find(y => y.iso == x)?.name} id={x} label={t('name')} name={x} onChange={handleChangeTraslation} />
                                                        {x !== ELanguages.en && (
                                                            <Stack alignItems={'end'}>
                                                                <IconButton aria-label={`delete-product-${x}`} onClick={() => handleClickRemoveTranslation(x)}>
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Stack>
                                </Stack>
                                <Stack width={'33%'} position='relative'>
                                    <Stack variant='scrollable'>
                                        {product
                                            .productPhysicalDatas!.filter(x => x.unitOfMeasure == unitOfMeasure)
                                            .map((x, i) => (
                                                <Accordion disableGutters>
                                                    <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                        <Typography>{t(`data ${i}`)}</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Input
                                                            id={drainHeight}
                                                            label={t(drainHeight, {
                                                                unitOfMeasure: productPD[drainHeight as TInputProductPhysicalDatas]?.unitOfMeasure[unitOfMeasure],
                                                            })}
                                                            isNumber
                                                            name={drainHeight}
                                                            value={x.drainHeight}
                                                            onChange={(e: any) => handleChange(e, x.id!)}
                                                        />
                                                        <Input
                                                            id={drainUnitWidth}
                                                            label={t(drainUnitWidth, {
                                                                unitOfMeasure: productPD[drainUnitWidth as TInputProductPhysicalDatas]?.unitOfMeasure[unitOfMeasure],
                                                            })}
                                                            isNumber
                                                            name={drainUnitWidth}
                                                            value={x.drainUnitWidth}
                                                            onChange={(e: any) => handleChange(e, x.id!)}
                                                        />
                                                        <Input
                                                            id={drainUnitLength}
                                                            label={t(drainUnitLength, {
                                                                unitOfMeasure: productPD[drainUnitLength as TInputProductPhysicalDatas]?.unitOfMeasure[unitOfMeasure],
                                                            })}
                                                            isNumber
                                                            name={drainUnitLength}
                                                            value={x.drainUnitLength}
                                                            onChange={(e: any) => handleChange(e, x.id!)}
                                                        />
                                                        <Input
                                                            id={maxUnitaryDrainageCapacity}
                                                            label={t(maxUnitaryDrainageCapacity, {
                                                                unitOfMeasure: productPD[maxUnitaryDrainageCapacity as TInputProductPhysicalDatas]?.unitOfMeasure[unitOfMeasure],
                                                            })}
                                                            isNumber
                                                            name={maxUnitaryDrainageCapacity}
                                                            value={x.maxUnitaryDrainageCapacity}
                                                            onChange={(e: any) => handleChange(e, x.id!)}
                                                        />
                                                        <Input
                                                            id={maximumAppliedLoad}
                                                            label={t(maximumAppliedLoad, {
                                                                unitOfMeasure: productPD[maximumAppliedLoad as TInputProductPhysicalDatas]?.unitOfMeasure[unitOfMeasure],
                                                            })}
                                                            isNumber
                                                            name={maximumAppliedLoad}
                                                            value={x.maximumAppliedLoad}
                                                            onChange={(e: any) => handleChange(e, x.id!)}
                                                        />
                                                        {product.productPhysicalDatas?.length! > 1 && (
                                                            <IconButton
                                                                style={{ justifyContent: 'end', width: '100%' }}
                                                                aria-label='remove-image-product'
                                                                onClick={() => setProduct({ ...product, productPhysicalDatas: product.productPhysicalDatas!.filter((y: any, u: any) => u !== i) })}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))}
                                        {product.isMaccaferriManufacturer && (
                                            <IconButton
                                                style={{ justifyContent: 'end' }}
                                                aria-label='remove-image-product'
                                                onClick={() =>
                                                    setProduct({
                                                        ...product,
                                                        productPhysicalDatas: [
                                                            ...product.productPhysicalDatas!,
                                                            unitOfMeasure == EUnitOfMeasure.Metric ? { ...newPhysicalDataMetric } : { ...newPhysicalDataImperial },
                                                        ],
                                                    })
                                                }>
                                                <AddIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>
                                <Stack width={'33%'} position='relative'>
                                    <Stack variant='scrollable'>
                                        {image?.map((x: any, i: any) => {
                                            let children: React.ReactNode;
                                            if (x) {
                                                children = (
                                                    <>
                                                        <Card variant='standard'>
                                                            <CardMedia
                                                                height={160}
                                                                component='img'
                                                                image={typeof image[i] === 'string' ? image[i] : URL.createObjectURL(image[i])}
                                                                alt='image-product'
                                                            />
                                                        </Card>
                                                        <IconButton
                                                            style={{ justifyContent: 'end' }}
                                                            aria-label='remove-image-product'
                                                            onClick={() => (image.length > 1 ? setImage(image.filter((y: any, u: any) => u !== i)) : setImage(['']))}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </>
                                                );
                                            } else {
                                                children = (
                                                    <>
                                                        <Stack height={160} justifyContent={'center'} style={{ border: `0.1rem solid ${variable.greyLight}` }}>
                                                            <IconButton aria-label='add-image-product' onClick={() => fileInputRef.current!.click()}>
                                                                <UploadIcon />
                                                            </IconButton>
                                                            <input onChange={e => handleImageChange(e, i)} multiple={false} ref={fileInputRef} type='file' hidden />
                                                        </Stack>
                                                        {image.length > 1 && (
                                                            <IconButton
                                                                style={{ justifyContent: 'end' }}
                                                                aria-label='remove-image-product'
                                                                onClick={() => setImage(image.filter((y: any, u: any) => u !== i))}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                    </>
                                                );
                                            }
                                            return (
                                                <Accordion disableGutters>
                                                    <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                        <Typography>{t(`picture ${i}`)}</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Stack direction={'column'} spacing={2}>
                                                            {children}
                                                        </Stack>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                        {image[0] !== '' && (
                                            <IconButton style={{ justifyContent: 'end' }} aria-label='remove-image-product' onClick={() => setImage([...image, ''])}>
                                                <AddIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                            <Stack alignItems='end'>
                                <Stack direction={'row'} spacing={2}>
                                    <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                        {t('back')}
                                    </Button>
                                    <LoadingButton loading={isSaveLoading} style={{ overflow: 'hidden' }} variant='orange' onClick={handleClickSave}>
                                        {t('save')}
                                    </LoadingButton>
                                </Stack>
                            </Stack>
                        </Stack>
                    </ProductContainer>
                    <ModalDrainageCapacity drainageCapacities={product.drainageCapacities!} onClose={handleCloseModal} onApply={handleClickApply} open={openModal} />
                </>
            )}
        </>
    );
};

export default isAdmin(ProductDetail);
